import { ContentLink, EpiContent } from '@api/types';
import {
  BlockProperty,
  ContentArea,
  HeaderCartButton,
  HeaderSearch,
  Image,
  LightningCartButton,
  SmartLink,
} from '@components';
import { Box, Grid, Skeleton, Stack, StackProps, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints, spacing } from '@themes/theme.helpers';
import React from 'react';

// STYLES
//--------
export const HeaderWrapper = styled(Box)`
  position: relative;
  position: sticky;
  z-index: 1199;
  top: 0;
  min-height: 56px;
  background-color: ${colors.background.white};
  box-shadow: 0px 17px 33px rgba(92, 115, 152, 0.1);
  transition: min-height 300ms linear;
  ${breakpoints.up('md')} {
    min-height: 92px;
  }
  ${breakpoints.down('md')} {
    border-bottom: 1px solid ${colors.primary.mainGreen};
  }
`;

const HeaderBar = styled(Box)`
  display: flex;
  z-index: 1010;
  max-width: 1440px;
  margin: 0 auto;
  justify-content: space-between;
  padding: 8px 24px;
  ${breakpoints.up('md')} {
    padding: 0 24px;
  }
`;

const HeaderLogo = styled(Box)`
  display: flex;
  transition: padding 300ms linear;
  min-width: 200px;
  display: flex;
  align-items: center;
  ${breakpoints.up('md')} {
    height: 92px;
  }
  a {
    display: block;
    height: 40px;
    ${breakpoints.up('md')} {
      height: 56px;
    }
    span {
      display: flex;
    }
  }
  img {
    height: 40px;
    width: auto;
    max-width: none;
    transition: height 200ms linear;
    ${breakpoints.up('md')} {
      height: 56px;
    }
  }
`;

const MainHeaderLinks = styled(Stack)<StackProps>`
  flex-grow: 1;
  max-height: 0;
  transition: all 200ms linear;
  display: none;
  opacity: 0;
  ${breakpoints.up('md')} {
    display: flex;
    max-height: 92px;
    opacity: 1;
    align-items: center;
  }
  .link-block a {
    width: max-content;
  }
`;

const MobileHeaderSearchBox = styled(Box)`
  box-sizing: border-box;
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: 1px solid ${colors.text.primary};
  overflow: hidden;
  padding-left: 16px;
  padding-right: 16px;
  transition: all 200ms linear;
  ${breakpoints.up('md')} {
    height: 0;
    padding: 0;
    border: 0;
    padding-left: 0;
    padding-right: 0;
  }
`;

// TYPES
//-------
interface HeaderBlockProps {
  title?: string;
  children?: React.ReactNode;
  content?: EpiContent;
  sideContent?: EpiContent;
  logo?: ContentLink;
}

// CONTENT
//---------
export const HeaderBlock = ({ logo, content, sideContent }: HeaderBlockProps) => {
  return (
    <HeaderWrapper component='header' className='site-header'>
      <HeaderContent logo={logo} content={content} sideContent={sideContent} />
      <MobileHeaderSearchBox>
        <HeaderSearch variant='mobile' guidValue='topSearchMobile' />
      </MobileHeaderSearchBox>
      <HeaderSubmenu id='header-submenu'></HeaderSubmenu>
    </HeaderWrapper>
  );
};

export const HeaderContent = ({ logo, content, sideContent }: HeaderBlockProps) => {
  return (
    <HeaderBar>
      <BlockProperty property='logo'>
        <HeaderLogo component='div'>
          <SmartLink href='/'>
            <Image image={logo} />
          </SmartLink>
        </HeaderLogo>
      </BlockProperty>
      <Stack
        direction='row'
        gap={3}
        sx={{
          width: '100%',
          justifyContent: 'end',
          alignItems: 'center',
          '& > *': {
            marginLeft: '0 !important',
          },
        }}
      >
        <MainHeaderLinks
          gap={3}
          direction='row'
          className='main-header-links'
          sx={{
            justifyContent: 'end',
            alignItems: 'center',
            '& > *': {
              marginLeft: '0 !important',
            },
          }}
        >
          <HeaderSearch variant='desktop' guidValue='topSearch' />
          <ContentArea content={content} />
        </MainHeaderLinks>
        <ContentArea content={sideContent} />
        <LightningCartButton />
        <HeaderCartButton />
      </Stack>
    </HeaderBar>
  );
};

/**
 * Container root for submenus (portals)
 */
export const HeaderSubmenu = styled(Box)``;

const StyledSkeleton = styled(Skeleton)`
  transform: none;
  width: 100%;
  height: ${spacing(3)};
  ${breakpoints.up('md')} {
    height: ${spacing(4)};
  }
`;

const StyledPlaceholder = styled(HeaderWrapper)`
  padding: 16px;
  ${breakpoints.up('md')} {
    padding: 32px 24px;
  }
`;

export const HeaderBlockPlaceholder = () => (
  <StyledPlaceholder className='site-header site-header-placeholder'>
    <Grid container spacing={2} className='fade-in-transition site-header-grid'>
      <Grid item xs={12} md={2}>
        <StyledSkeleton />
      </Grid>
      <Grid item xs={0} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
        <StyledSkeleton />
      </Grid>
      <Grid item xs={0} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
        <StyledSkeleton />
      </Grid>
      <Grid item xs={0} md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
        <StyledSkeleton />
      </Grid>
    </Grid>
  </StyledPlaceholder>
);
